import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Clock,
  Eye,
  Filter,
  Globe,
  Headphones,
  MessageSquare,
  RefreshCw,
  Search,
  X
} from 'lucide-react'
import { apiService } from '../../services/api'
import { toastError } from '../../services/errorUtils'
import { formatDuration, getStatusBadgeClass, getStatusLabel } from '../../utils/callPresentation'
import type { AgentOption, ConversationAnalysis, ConversationMessage, UnknownRecord } from '../../types/aicall'

interface Conversation {
  id: string
  agent_id: string
  agent_name?: string
  status: string
  status_kind?: string
  status_label?: string
  duration: number
  duration_label?: string
  created_at: string
  updated_at?: string
  message_count?: number
  call_successful?: string
  transcript_summary?: string
  call_summary_title?: string
  direction?: string
  original_title?: string
  original_summary?: string
  translated?: boolean
}

interface ConversationDetail {
  id: string
  agent_id: string
  status: string
  status_kind?: string
  status_label?: string
  duration: number
  duration_label?: string
  created_at: string
  messages: ConversationMessage[]
  evaluation_result?: string
  has_audio?: boolean
  has_user_audio?: boolean
  has_response_audio?: boolean
  user_id?: string
  analysis?: ConversationAnalysis
  conversation_data?: UnknownRecord
  transcript_summary?: string
  call_summary_title?: string
}

const languageOptions = [
  { value: 'pt-BR', label: 'Português (Brasil)' },
  { value: 'en-US', label: 'English (US)' },
  { value: 'es-ES', label: 'Español' }
]

export default function Conversations() {
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [agents, setAgents] = useState<AgentOption[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedAgentId, setSelectedAgentId] = useState('all')
  const [showTranscriptModal, setShowTranscriptModal] = useState(false)
  const [selectedConversation, setSelectedConversation] = useState<ConversationDetail | null>(null)
  const [loadingTranscript, setLoadingTranscript] = useState(false)
  const [detectedLanguage, setDetectedLanguage] = useState<string>('')
  const [selectedLanguage, setSelectedLanguage] = useState<string>('pt-BR')
  const [showLanguageSelector, setShowLanguageSelector] = useState(false)

  const loadAgents = useCallback(async () => {
    try {
      const response = await apiService.getAgents()
      setAgents(response.data.data || [])
    } catch (error) {
      console.error('Erro ao carregar agentes:', error)
    }
  }, [])

  const loadConversations = useCallback(async () => {
    try {
      setLoading(true)
      const response = await apiService.getConversations(selectedAgentId, 100, 0, selectedLanguage)
      setConversations(response.data.data || [])

      if (response.data.language) {
        setDetectedLanguage(response.data.language)
      }
    } catch (error) {
      toastError(error, 'Erro ao carregar conversas')
    } finally {
      setLoading(false)
    }
  }, [selectedAgentId, selectedLanguage])

  useEffect(() => {
    const savedLanguage = localStorage.getItem('aicall-language')
    if (savedLanguage) {
      setSelectedLanguage(savedLanguage)
    }
    void loadAgents()
  }, [loadAgents])

  useEffect(() => {
    void loadConversations()
  }, [loadConversations])

  const loadConversationDetails = async (conversation: Conversation) => {
    try {
      setLoadingTranscript(true)

      try {
        const overviewResponse = await apiService.getConversationOverview(conversation.agent_id, conversation.id)
        const overviewData = overviewResponse.data.data

        if (overviewData.overview?.summary) {
          setSelectedConversation({
            ...overviewData,
            transcript_summary: overviewData.overview.summary,
            call_summary_title: overviewData.call_summary_title || conversation.call_summary_title
          })
        } else {
          const response = await apiService.getConversation(conversation.agent_id, conversation.id)
          setSelectedConversation(response.data.data)
        }
      } catch {
        const response = await apiService.getConversation(conversation.agent_id, conversation.id)
        setSelectedConversation(response.data.data)
      }

      setShowTranscriptModal(true)
    } catch (error) {
      toastError(error, 'Erro ao carregar transcrição da conversa')
    } finally {
      setLoadingTranscript(false)
    }
  }

  const filteredConversations = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase()
    const agentNames = new Map(agents.map((agent) => [agent.id, agent.name || agent.agent_id || agent.id]))

    return conversations
      .map((conversation) => ({
        ...conversation,
        agent_name: conversation.agent_name || agentNames.get(conversation.agent_id) || conversation.agent_id
      }))
      .filter((conversation) => {
        if (!normalizedSearch) return true

        return [
          conversation.id,
          conversation.agent_id,
          conversation.agent_name,
          conversation.call_summary_title,
          conversation.transcript_summary
        ].some((value) => String(value || '').toLowerCase().includes(normalizedSearch))
      })
  }, [agents, conversations, searchTerm])

  const handleLanguageChange = (language: string) => {
    setSelectedLanguage(language)
    localStorage.setItem('aicall-language', language)
    setShowLanguageSelector(false)
  }

  const currentLanguageLabel =
    languageOptions.find((opt) => opt.value === (selectedLanguage || detectedLanguage || 'pt-BR'))?.label ??
    (selectedLanguage || detectedLanguage || 'pt-BR')

  const getStatusBadge = (conversation: Conversation | ConversationDetail) => (
    <span className={getStatusBadgeClass(conversation)}>{getStatusLabel(conversation)}</span>
  )

  const getConversationDuration = (conversation: Conversation | ConversationDetail) =>
    conversation.duration_label || formatDuration(conversation.duration)

  if (loading) {
    return (
      <div className="page-shell">
        <div className="page-header">
          <div>
            <h1 className="page-title">Conversas</h1>
            <p className="page-description">
              Leia transcrições, resumos e resultados das chamadas feitas pelos agentes.
            </p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="card animate-pulse">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="h-4 w-3/4 rounded bg-slate-200" />
                  <div className="h-3 w-1/2 rounded bg-slate-100" />
                </div>
                <div className="h-5 w-16 rounded-full bg-slate-200" />
              </div>
              <div className="mt-3 space-y-1.5">
                <div className="h-3 w-full rounded bg-slate-100" />
                <div className="h-3 w-5/6 rounded bg-slate-100" />
                <div className="h-3 w-4/6 rounded bg-slate-100" />
              </div>
              <div className="mt-4 space-y-2">
                <div className="h-3 w-2/3 rounded bg-slate-100" />
                <div className="h-3 w-1/2 rounded bg-slate-100" />
              </div>
              <div className="mt-4 h-8 w-full rounded bg-slate-200" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <h1 className="page-title">Conversas</h1>
          <p className="page-description">
            Leia transcrições, resumos e resultados das chamadas feitas pelos agentes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowLanguageSelector((value) => !value)}
              className="btn-secondary"
              aria-label="Selecionar idioma"
            >
              <Globe className="h-4 w-4" />
              {currentLanguageLabel}
            </button>
            {showLanguageSelector && (
              <div className="absolute right-0 top-full z-20 mt-1 w-52 rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                {languageOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleLanguageChange(option.value)}
                    className={`block w-full px-4 py-2 text-left text-sm transition hover:bg-slate-50 ${
                      selectedLanguage === option.value ? 'bg-primary-50 font-semibold text-primary-700' : 'text-slate-700'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button type="button" className="btn-secondary" onClick={() => void loadConversations()}>
            <RefreshCw className="h-4 w-4" />
            Atualizar
          </button>
        </div>
      </div>

      <div className="toolbar">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            aria-label="Buscar conversas"
            placeholder="Buscar conversas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-9"
          />
        </div>
        <select
          value={selectedAgentId}
          onChange={(e) => setSelectedAgentId(e.target.value)}
          className="input sm:max-w-xs"
          aria-label="Filtrar por agente"
        >
          <option value="all">Todos os agentes</option>
          {agents.map((agent) => (
            <option key={agent.id} value={agent.id}>
              {agent.name || agent.agent_id || agent.id}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => {
            setSearchTerm('')
            setSelectedAgentId('all')
            setSelectedLanguage(detectedLanguage || 'pt-BR')
          }}
        >
          <Filter className="h-4 w-4" />
          Limpar
        </button>
      </div>

      <section className="surface">
        <div className="panel-header">
          <div>
            <h2 className="panel-title">Conversas ({filteredConversations.length})</h2>
            <p className="panel-subtitle">Cartões compactos para revisar resultado, duração e transcrição.</p>
          </div>
        </div>

        <div className="p-4 sm:p-5">
          {filteredConversations.length === 0 ? (
            <div className="empty-state">
              <MessageSquare className="mb-4 h-12 w-12 text-slate-300" />
              <h3 className="text-sm font-semibold text-slate-950">Nenhuma conversa encontrada</h3>
              <p className="mt-1 text-sm text-slate-500">
                {searchTerm
                  ? 'Tente ajustar os filtros de busca.'
                  : 'Nenhuma conversa encontrada. Faça uma ligação ou atualize a página.'}
              </p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {filteredConversations.map((conversation) => (
                <article
                  key={conversation.id}
                  className="card transition-all hover:border-primary-200 hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate font-semibold text-slate-950">
                        {conversation.agent_name || conversation.agent_id}
                      </h3>
                      <p className="mt-0.5 truncate text-xs text-slate-400">{conversation.id}</p>
                    </div>
                    <div className="flex-none">{getStatusBadge(conversation)}</div>
                  </div>

                  <p className="mt-2 line-clamp-2 text-sm text-slate-700">
                    {conversation.call_summary_title || conversation.transcript_summary || 'Sem resumo disponível.'}
                  </p>

                  {conversation.transcript_summary && conversation.call_summary_title && (
                    <p className="mt-1 line-clamp-2 text-xs text-slate-500">
                      {conversation.transcript_summary}
                    </p>
                  )}

                  <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      {getConversationDuration(conversation)}
                    </span>
                    <span>·</span>
                    <span>{new Date(conversation.created_at).toLocaleDateString('pt-BR')}</span>
                    {conversation.message_count != null && (
                      <>
                        <span>·</span>
                        <span className="flex items-center gap-1">
                          <MessageSquare className="h-3.5 w-3.5" />
                          {conversation.message_count}
                        </span>
                      </>
                    )}
                  </div>

                  {(conversation.direction || conversation.translated) && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {conversation.direction && (
                        <span className="badge badge-info">
                          {conversation.direction === 'inbound' ? 'Entrada' : 'Saída'}
                        </span>
                      )}
                      {conversation.translated && (
                        <span className="badge badge-info">Traduzido</span>
                      )}
                    </div>
                  )}

                  <div className="mt-4 border-t border-slate-100 pt-3">
                    <button
                      type="button"
                      onClick={() => void loadConversationDetails(conversation)}
                      disabled={loadingTranscript}
                      className="btn-secondary w-full"
                    >
                      <Eye className="h-4 w-4" />
                      Ver transcrição
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {showTranscriptModal && (
        <div className="modal-backdrop fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center px-4 py-6 sm:p-0">
            <div
              className="absolute inset-0"
              onClick={() => setShowTranscriptModal(false)}
              aria-hidden="true"
            />
            <div className="modal-panel relative max-h-[90vh] max-w-3xl overflow-hidden flex flex-col">
              <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-6 py-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-950">Transcrição da conversa</h3>
                  <p className="mt-0.5 text-sm text-slate-500">Mensagens, status e análise retornados pela ElevenLabs.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowTranscriptModal(false)}
                  className="btn-icon"
                  aria-label="Fechar transcrição"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-5">
                {loadingTranscript && (
                  <div className="flex min-h-48 items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
                  </div>
                )}

                {!loadingTranscript && !selectedConversation && (
                  <div className="flex min-h-48 items-center justify-center text-sm text-slate-500">
                    Não foi possível carregar a transcrição.
                  </div>
                )}

                {!loadingTranscript && selectedConversation && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <MetaItem label="ID" value={selectedConversation.id} mono />
                      <MetaItem label="Agente" value={selectedConversation.agent_id} mono />
                      <MetaItem label="Duração" value={getConversationDuration(selectedConversation)} />
                      <MetaItem label="Status" value={getStatusLabel(selectedConversation)} />
                      <MetaItem label="Data" value={new Date(selectedConversation.created_at).toLocaleString('pt-BR')} />
                      <MetaItem label="Mensagens" value={selectedConversation.messages?.length || 0} />
                    </div>

                    <div className="scrollbar-thin max-h-80 overflow-y-auto rounded-lg border border-slate-200 p-4">
                      <h4 className="mb-3 text-sm font-semibold text-slate-950">Mensagens</h4>
                      {selectedConversation.messages && selectedConversation.messages.length > 0 ? (
                        <div className="space-y-3">
                          {selectedConversation.messages.map((message, index) => {
                            const isUser = message.role === 'user'
                            return (
                              <div
                                key={index}
                                className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}
                              >
                                <span className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  {isUser ? 'Usuário' : 'Agente'}
                                  {message.timestamp && (
                                    <span className="ml-2 font-normal normal-case tracking-normal text-slate-400">
                                      {new Date(message.timestamp).toLocaleTimeString('pt-BR')}
                                    </span>
                                  )}
                                </span>
                                <div
                                  className={`max-w-[80%] px-4 py-2.5 text-sm leading-6 text-slate-900 ${
                                    isUser
                                      ? 'rounded-l-2xl rounded-br-2xl bg-primary-50'
                                      : 'rounded-r-2xl rounded-bl-2xl bg-slate-100'
                                  }`}
                                >
                                  {message.content || message.text || message.message}
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      ) : (
                        <div className="empty-state py-8">
                          <MessageSquare className="mb-3 h-10 w-10 text-slate-300" />
                          <p className="text-sm text-slate-500">Nenhuma mensagem encontrada nesta conversa.</p>
                        </div>
                      )}
                    </div>

                    {(selectedConversation.has_audio || selectedConversation.has_user_audio || selectedConversation.has_response_audio) && (
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                        <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-950"><Headphones className="h-4 w-4" /> Áudio da conversa</h4>
                        <audio
                          controls
                          preload="metadata"
                          className="w-full"
                          src={`${import.meta.env.VITE_API_URL || 'http://localhost:3008/api'}/conversations/${selectedConversation.agent_id}/${selectedConversation.id}/audio`}
                        >
                          Seu navegador não suporta o player de áudio.
                        </audio>
                      </div>
                    )}

                    {selectedConversation.analysis && (
                      <div className="rounded-lg border border-primary-100 bg-primary-50 p-4">
                        <h4 className="mb-2 text-sm font-semibold text-slate-950">Análise da conversa</h4>
                        <div className="space-y-2 text-sm text-slate-700">
                          <div className="flex items-center gap-2">
                            <strong>Resultado:</strong>
                            {selectedConversation.evaluation_result ? (
                              <span
                                className={
                                  selectedConversation.evaluation_result.toLowerCase().includes('success') ||
                                  selectedConversation.evaluation_result.toLowerCase().includes('pass') ||
                                  selectedConversation.evaluation_result.toLowerCase().includes('true')
                                    ? 'badge-success'
                                    : 'badge-danger'
                                }
                              >
                                {selectedConversation.evaluation_result}
                              </span>
                            ) : (
                              <span className="text-slate-500">N/A</span>
                            )}
                          </div>
                          {selectedConversation.analysis.summary && (
                            <p>
                              <strong>Resumo:</strong> {selectedConversation.analysis.summary}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end border-t border-slate-100 bg-slate-50 px-6 py-4">
                <button type="button" className="btn-primary" onClick={() => setShowTranscriptModal(false)}>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


function MetaItem({ label, value, mono = false }: { label: string; value: string | number; mono?: boolean }) {
  return (
    <div className="min-w-0 rounded bg-slate-50 p-2">
      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className={`mt-1 break-words text-sm font-semibold text-slate-950 ${mono ? 'font-mono' : ''}`}>{value}</dd>
    </div>
  )
}
